import { useEffect, useMemo, useState } from "react";
import { Offcanvas, Form, Button } from "react-bootstrap";
import { InputMask } from "@react-input/mask";
import { toast } from "react-toastify";
import {
  AutomatedGiftTab,
  useAddAutomatedRecipientMutation,
} from "../../../features/automatedGifts/automatedGiftsApi";
import { Campaign } from "../../../features/campaign/campaignTypes";
import { giftTypeOptions } from "../../../features/emailTemplate/emailTypes";
import { useGetPriceListQuery } from "../../../features/users/usersApi";
import {
  priceOptionsForGiftType,
  resolveDefaultPriceId,
} from "../utils";
import AgSelect from "./AgSelect";

interface AddRecipientDrawerProps {
  show: boolean;
  type: AutomatedGiftTab;
  campaign?: Campaign;
  onHide: () => void;
}

const giftTypeSelectOptions = giftTypeOptions.map((opt) => ({
  value: opt.value,
  label: opt.label,
}));

const isHomeAnniversary = (type: AutomatedGiftTab) =>
  type === "home-anniversary";

const validateBirthdayDate = (val: string) => {
  if (!/^\d{2}\/\d{2}$/.test(val)) return false;
  const [monthStr, dayStr] = val.split("/");
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (month < 1 || month > 12) return false;
  const daysInMonth = new Date(2000, month, 0).getDate();
  return day >= 1 && day <= daysInMonth;
};

const validateHomeAnniversaryDate = (val: string) => {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return false;
  const [monthStr, dayStr, yearStr] = val.split("/");
  const month = Number(monthStr);
  const day = Number(dayStr);
  const year = Number(yearStr);
  if (year < 1900 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  return day >= 1 && day <= daysInMonth;
};

const AddRecipientDrawer = ({
  show,
  type,
  campaign,
  onHide,
}: AddRecipientDrawerProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [giftType, setGiftType] = useState("");
  const [priceId, setPriceId] = useState("");
  const [addRecipient, { isLoading }] = useAddAutomatedRecipientMutation();
  const { data: priceList } = useGetPriceListQuery(undefined);
  const withYear = isHomeAnniversary(type);

  const priceOptions = useMemo(
    () => priceOptionsForGiftType(priceList?.data, giftType),
    [priceList?.data, giftType],
  );

  useEffect(() => {
    if (!show) return;
    const nextGiftType = campaign?.gift_type || "";
    setName("");
    setEmail("");
    setMobile("");
    setEventDate("");
    setGiftType(nextGiftType);
    setPriceId(
      resolveDefaultPriceId(
        priceList?.data,
        nextGiftType,
        campaign?.price_id,
      ),
    );
  }, [show, type, campaign?.gift_type, campaign?.price_id, priceList?.data]);

  useEffect(() => {
    if (!giftType) {
      setPriceId("");
      return;
    }
    setPriceId((current) =>
      resolveDefaultPriceId(priceList?.data, giftType, current),
    );
  }, [giftType, priceList?.data, priceOptions]);

  const handleGiftTypeChange = (nextType: string) => {
    setGiftType(nextType);
    setPriceId(resolveDefaultPriceId(priceList?.data, nextType, null));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const dateOk = withYear
      ? validateHomeAnniversaryDate(eventDate.trim())
      : validateBirthdayDate(eventDate.trim());

    if (!dateOk) {
      toast.error(
        withYear
          ? "Enter a valid event date (MM/DD/YYYY)"
          : "Enter a valid event date (MM/DD)",
      );
      return;
    }

    try {
      await addRecipient({
        type,
        data: {
          name: name.trim(),
          email: email.trim(),
          mobile: mobile.trim() || undefined,
          event_date: eventDate.trim(),
          gift_type: giftType || undefined,
          price_id: priceId || undefined,
        },
      }).unwrap();
      toast.success("Recipient added");
      onHide();
    } catch (error: any) {
      toast.error(error?.data?.message || "Could not add recipient");
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: "min(520px, 100vw)" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="settings-header">
          Add recipient
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form onSubmit={handleSubmit} className="d-flex flex-column h-100">
          <div className="flex-grow-1">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mobile (optional)</Form.Label>
              <Form.Control
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Phone number"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Event date ({withYear ? "MM/DD/YYYY" : "MM/DD"})
              </Form.Label>
              <InputMask
                component={Form.Control}
                mask={withYear ? "__/__/____" : "__/__"}
                replacement={{ _: /\d/ }}
                required
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                placeholder={withYear ? "08/15/2019" : "08/15"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Gift type</Form.Label>
              <AgSelect
                value={giftType}
                options={giftTypeSelectOptions}
                onChange={handleGiftTypeChange}
                placeholder="Select gift type"
                isClearable
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Price</Form.Label>
              <AgSelect
                value={priceId}
                options={priceOptions}
                onChange={setPriceId}
                placeholder={
                  giftType ? "Select price" : "Choose gift type first"
                }
                isDisabled={!giftType}
              />
            </Form.Group>
          </div>
          <div className="d-flex justify-content-end gap-2 pt-3 border-top">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={onHide}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: "#b8894a",
                borderColor: "#b8894a",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              {isLoading ? "Saving…" : "Add recipient"}
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddRecipientDrawer;
